import { NgFor, NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ConnectionStatus } from '@capacitor/network';
import { ActivityFormModalComponent, ActivityLocationDisplayComponent } from '@curbnturf/activity-component';
import { ActivityLocationFacade } from '@curbnturf/activity/src/lib/+state/activity.facade';
import { MapIconsComponent } from '@curbnturf/custom-svgs/src/lib/map-icons/map-icons.component';
import { MiscSvgComponent } from '@curbnturf/custom-svgs/src/lib/misc-svg/misc-svg.component';
import { ActivityLocation, DEFAULT_IMAGE_URL, HelperFunctions, IPhoto, IUser, PhotoSizes } from '@curbnturf/entities';
import { CheckedComponent } from '@curbnturf/form-helpers/src/lib/components/checked/checked.component';
import { PhotoService } from '@curbnturf/photo';
import { HideConfirmationModalComponent } from '@curbnturf/site-components/src/lib/hide-confirmation-modal/hide-confirmation-modal.component';
import { StatusFacade } from '@curbnturf/status';
import { DeleteConfirmationModalComponent } from '@curbnturf/ui/src/lib/delete-confirmation-modal/delete-confirmation-modal.component';
import { UserFacade } from '@curbnturf/user/src/lib/+state/user.facade';
import { IonicModule, ModalController } from '@ionic/angular';
import { Subscription } from 'rxjs';

@Component({
  selector: 'curbnturf-poi-list-item',
  templateUrl: './poi-list-item.component.html',
  styleUrls: ['./poi-list-item.component.scss'],
  standalone: true,
  imports: [
    CheckedComponent,
    DeleteConfirmationModalComponent,
    HideConfirmationModalComponent,
    IonicModule,
    NgIf,
    MapIconsComponent,
    MiscSvgComponent,
    NgFor,
    RouterLink,
  ],
})
export class PoiListItemComponent implements OnInit, OnDestroy {
  @Input() poi: ActivityLocation;

  @Output() poiRemoved: EventEmitter<string> = new EventEmitter<string>();

  public photoUrl: string = DEFAULT_IMAGE_URL;

  public completePercent: number = 0;

  user: IUser;

  connectionStatus: ConnectionStatus = { connected: true, connectionType: 'wifi' };

  subscriptions: Subscription[] = [];

  getPhotoUrl = HelperFunctions.getPhotoUrlSquareMedium;

  constructor(
    private activityLocationFacade: ActivityLocationFacade,
    private cd: ChangeDetectorRef,
    private modalController: ModalController,
    private photoService: PhotoService,
    private statusFacade: StatusFacade,
    private userFacade: UserFacade,
  ) {}

  async ngOnInit() {
    if (this.photoUrl === DEFAULT_IMAGE_URL) {
      // Don't reload the image every time this component is viewed.
      await this.getPhotoUrlFromKey(this.poi?.photos?.[0]);
    }
    this.subscriptions.push(
      this.statusFacade.networkStatus$.subscribe((status) => {
        this.connectionStatus = status;
      }),
      this.userFacade.currentUserOnce$.subscribe((user) => {
        this.user = user;
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => {
      if (!sub.closed) {
        sub.unsubscribe();
      }
    });
  }

  async getPhotoUrlFromKey(photo?: IPhoto): Promise<void> {
    if (photo) {
      if (this.poi.id) {
        this.photoUrl = await this.photoService.getSavedPhotoUrl(photo, PhotoSizes.FlatSmall);
      } else if (this.poi.syncId) {
        this.photoUrl = await this.photoService.getPhotoOffline(photo);
      }
    } else {
      this.photoUrl = DEFAULT_IMAGE_URL;
    }

    this.cd.detectChanges();
  }

  removePoi(id?: number | string): void {
    if (id) {
      this.activityLocationFacade.remove(id as number);
      this.poiRemoved.emit(this.poi.syncId);
    }
  }

  removePoiFunction(id?: number | string): () => void {
    return () => this.removePoi(id);
  }

  async openPoiViewModal() {
    const modal = await this.modalController.create({
      component: ActivityLocationDisplayComponent,
      componentProps: {
        activity: this.poi,
      },
    });
    await modal.present();
  }

  async openPoiEditModal() {
    const modal = await this.modalController.create({
      component: ActivityFormModalComponent,
      componentProps: {
        activity: this.poi,
      },
    });
    await modal.present();
  }
}
